import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import React, { ChangeEvent, KeyboardEvent, useState } from 'react';

import { Styles } from './includes/styles';

type GoToPageProps = {
  currentPage?: number;
  navigate?: (page: number) => void;
  totalPages?: number;
};

/**
 * GoToPage
 *
 * Allows the user to navigate directly to a specific page number of results within a data table.
 */
export function GoToPage({ currentPage = 1, navigate = () => {}, totalPages = 0 }: GoToPageProps) {
  const [requestedPage, setRequestedPage] = useState<string>(currentPage.toString());

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Allow empty string or numbers not starting with 0
    if (/^\d*$/.test(value)) {
      // Prevent leading zero or value of 0
      const isEmpty = value === '';
      const numericValue = Number(value);
      const isPositive = numericValue > 0;
      const hasNoLeadingZero = !/^0\d+/.test(value);
      const isWithinRange = numericValue <= totalPages;

      if (isEmpty || (isPositive && hasNoLeadingZero && isWithinRange)) {
        setRequestedPage(value);
      }
    }
  };

  const handlePageChange = () => {
    const pageNum = Number(requestedPage);

    if (!requestedPage || Number.isNaN(pageNum) || pageNum === 0) {
      return;
    }

    let value = pageNum;

    if (value > totalPages) {
      value = totalPages;
    }

    if (value < 1) {
      value = 1;
    }

    if (value !== currentPage) {
      navigate(value - 1);
    }
  };

  return (
    <ErrorHandler location="src/shared/ui/components/TableQuery/components/Header/components/GoToPage/GoToPage.tsx">
      <Styles>
        <TextInput
          formatter={(value) => {
            return value.replace(/[^0-9]/g, '');
          }}
          hideLabel
          inputRightElement={
            <Button onClick={handlePageChange} type="submit" variant="solid">
              Go
            </Button>
          }
          label=""
          leftAddOn="Page"
          onChange={handleInputChange}
          onKeyDown={async (event: KeyboardEvent<HTMLInputElement>) => {
            if (event?.key === 'Enter') {
              handlePageChange();
            }
          }}
          value={requestedPage}
          width="11em"
        />
      </Styles>
    </ErrorHandler>
  );
}
